<template>
    <div>
        <div class="topbar topbar--full pt-4">
            <router-link to="/settings" class="link-back">All Settings</router-link>
            <h2 class="heading1 mb-0">Admin</h2>
        </div>
        
        <div class="admin-page">
            <Header></Header>
            
            <div class="admin-page__content">
                <form class="form form--report">
                    <h4 class="form--report__title">Generate A Report</h4>
                    
                    <div v-if="exportType" class="form--report__section">
                        <label class="form__label">Report type</label>
                        <select v-model="exportType" class="form-control form-control--mid">
                            <option v-for="type in exportTypes" :value="type.value" :key="type.value">{{type.label}}</option>
                        </select>
                    </div>

                    <template v-if="availableFilters['period'].includes(exportType) || availableFilters['users'].includes(exportType)">
                        <label class="form__label">Filters</label>

                        <div class="form--report__filters">
                            <div>
                                <div v-if="availableFilters['period'].includes(exportType)">
                                    <label class="form__label">Timeframe</label>

                                    <select v-model="filter.period" class="form-control form-control--light">
                                        <option v-for="period in periodTypes" :value="period.value" :key="period.value">{{period.label}}</option>
                                    </select>
                                </div>

                                <div v-if="availableFilters['users'].includes(exportType)" class="form--report__filters__element">
                                    <label class="form__label">Users</label>

                                    <GroupsAndUsersList
                                        @selectedChanged="setUsers"
                                    />
                                </div>

                                <div v-if="availableFilters['trackers'].includes(exportType)" class="form--report__filters__element">
                                    <label class="form__label">Trackers</label>

                                    <VueMultiselect
                                        v-model="filter.trackers"
                                        :multiple="true"
                                        :options="trackersList"
                                        select-label=""
                                        deselect-label=""
                                        placeholder="Type to search your trackers"
                                        label="name"
                                        track-by="name"
                                        class="multiselect-mid selectbox selectbox-mid"
                                    >
                                        <template slot="option" slot-scope="props">
                                            <span v-b-popover.hover.top="props.option.created_by + ': ' + props.option.created_at">{{props.option.name}}</span>
                                        </template>
                                    </VueMultiselect>
                                </div>
                            </div>

                            <span v-if="exportType === 'interactions'" class="input-checkbox">
                                <input type="checkbox" v-model="filter.useInteractionDate" id="ich-filter"> 
                                <label class="mt-3" for="ich-filter">Filter by Interaction Date</label>
                            </span>
                        </div>
                    </template>

                    <template v-if="['interactions','tracked-startups','startup-profile-fields'].includes(exportType)">
                        <label class="form__label">Profile Custom Fields</label>

                        <custom-fields-list
                            fields-type="company_diligence"
                            :checked-by-default="exportType === 'startup-profile-fields'"
                            @fields-selected="setProfileFields"
                        />
                    </template>

                    <template v-if="['tracked-startups'].includes(exportType)">
                        <label class="form__label">Tracker Diligence Fields</label>

                        <custom-fields-list
                            fields-type="tracker_diligence"
                            @fields-selected="setTrackerFields"
                        />
                    </template>

                    <div class="text-center pt-5">
                        <primary-button
                            :disabled="!canProcess"
                            caption="Generate report"
                            loading-caption="Generating report..."
                            :loading="loading"
                            @click="generateReport"
                        />
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script>
import Header from "@/views/settings/admin/AdminHeader";
import GroupsAndUsersList from "@/components/GroupsAndUsersList.vue";
import interactionsService from "@/services/interactions.service";
import trackersCompaniesService from "@/services/trackers-companies.service";
import startupsService from "@/services/startups.service";
import contactsService from "@/services/contacts.service";
import PrimaryButton from "@/components/ui/PrimaryButton.vue";
import CustomFieldsList from "@/components/customFields/CustomFieldsList.vue";
import exportsService from "@/services/exports.service";
import trackersService from "@/services/trackers.service";
import {trackerTypes} from "@/constants/trackers";
import VueMultiselect from "vue-multiselect";

const defaultExportTypes = [
    {
        value: 'interactions',
        label: 'Notes and Interactions'
    },
    {
        value: 'trackers-startups-stages',
        label: 'Tracker Startup Stages'
    },
    {
        value: 'tracked-startups',
        label: 'Tracked Startups'
    },
    {
        value: 'added-startups',
        label: 'Added Startup'
    },
    {
        value: 'custom-contacts',
        label: 'Custom Startups Contacts'
    },
    //Enable temporary Startup profiles export for all
    {
        value: 'startup-profile-fields',
        label: 'Startup Profile Fields'
    },
];

export default {
    components: {
        VueMultiselect,
        CustomFieldsList,
        PrimaryButton,
        Header,
        GroupsAndUsersList,
    },

    data() {
        return {
            exportType: 'interactions',
            usersList: [],

            filter: {
                period: this.constants.commonFilterPeriods[0].value,
                users: [],
                trackers: [],
                useInteractionDate: false,
            },

            profileFields: [],
            trackerFields: [],
            trackersList: [],

            availableFilters: {
                'period': ['interactions','trackers-startups-stages','tracked-startups','added-startups','custom-contacts','startup-profile-fields'],
                'users': ['interactions','trackers-startups-stages','tracked-startups','added-startups','custom-contacts','startup-profile-fields'],
                'trackers': ['trackers-startups-stages'],
            },

            loading: false,
        }
    },

    computed: {
        canProcess() {
            return !(this.exportType === 'startup-profile-fields' && !this.profileFields.length);
        },

        exportTypes() {
            let exportTypes = defaultExportTypes;

            if (this.$store.getters.isCoPaceEnterprise) {
                exportTypes = [
                    ...exportTypes,
                    {
                        value: 'co-pace-pocs',
                        label: 'Co-Pace PoCs'
                    },
                    {
                        value: 'co-pace-requests',
                        label: 'Co-Pace Request trackers'
                    },
                    {
                        value: 'startup-profile-fields',
                        label: 'Startup Profile Fields'
                    },
                ]
            }

            return exportTypes;
        },

        periodTypes() {
            let periodTypes = this.constants.commonFilterPeriods;

            periodTypes.splice(4, 0, {
                value: 'prev_quarter',
                label: 'Previous quarter'
            },);

            return periodTypes;
        }
    },

    mounted() {
        trackersService.getList([trackerTypes.PRIVATE, trackerTypes.PUBLIC]).then(response => {
            this.trackersList = response.data;
        });
    },

    methods: {
        generateReport() {
            this.loading = true;

            if (this.exportType === 'interactions') {
                interactionsService.exportCSV(this.filter, this.profileFields).then(response => {
                    this.fileHelper.download(response.data, 'interactions.csv')
                }).finally(() => {
                    this.loading = false
                })
            } else if (this.exportType === 'trackers-startups-stages') {
                trackersCompaniesService.exportTrackersStartupsStages(this.filter).then(response => {
                    this.fileHelper.download(response.data, 'startups-stages.csv')
                }).finally(() => {
                    this.loading = false
                })
            } else if (this.exportType === 'tracked-startups') {
                trackersCompaniesService.exportTrackedStartups(this.filter, this.profileFields, this.trackerFields).then(response => {
                    this.fileHelper.download(response.data, 'tracked-startups.csv')
                }).finally(() => {
                    this.loading = false
                })
            } else if (this.exportType === 'added-startups') {
                startupsService.exportAddedStartups(this.filter).then(response => {
                    this.fileHelper.download(response.data, 'added-startups.csv')
                }).finally(() => {
                    this.loading = false
                })
            } else if (this.exportType === 'custom-contacts') {
                contactsService.exportStartupsCustomContacts(this.filter).then(response => {
                    this.fileHelper.download(response.data, 'custom-startups-contacts.csv')
                }).finally(() => {
                    this.loading = false
                })
            } else if (this.exportType === 'co-pace-pocs') {
                exportsService.exportCoPaceReport(this.exportType).then(response => {
                    this.fileHelper.download(response.data, 'Co-Pace PoCs.csv')
                }).finally(() => {
                    this.loading = false
                })
            } else if (this.exportType === 'co-pace-requests') {
                exportsService.exportCoPaceReport(this.exportType).then(response => {
                    this.fileHelper.download(response.data, 'Co-Pace Request trackers.csv')
                }).finally(() => {
                    this.loading = false
                })
            } else if (this.exportType === 'startup-profile-fields') {
                startupsService.exportProfileFields(this.filter, this.profileFields).then(response => {
                    this.fileHelper.download(response.data, 'Startup Profile Fields.csv')
                }).finally(() => {
                    this.loading = false
                })
            }
        },

        setProfileFields(fieldsIds) {
            this.profileFields = fieldsIds;
        },

        setTrackerFields(fieldsIds) {
            this.trackerFields = fieldsIds;
        },

        setUsers(items) {
            this.filter.users = items.map(item => ({
                type: item.type,
                id: item.id
            }))
        }
    }
}
</script>

<style scoped>

</style>
